import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../../hooks/useBackendApi';
import { RequestOptions } from '../../../../../types/httpRequests';
import { AccountLocation, AccountType } from '../../../../../types/account';
import AccountLocationNameDescription from './AccountLocationNameDescription';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import LocationDrawer, { LocationDrawerPayload } from './LocationDrawer';
import GhostButton from '../../../../../components/common/buttons/GhostButton';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  location_id: string;
};

type LocationDetailsResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  account?: AccountType;
  location?: AccountLocation;
  user_org_roles?: string[];
  user_acc_roles?: string[];
};

export default function AccountLocationDetails(): JSX.Element {
  const { org_path_name, acc_path_name, location_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [account, setAccount] = useState<AccountType | null>(null);
  const [location, setLocation] = useState<AccountLocation | null>(null);
  const [viewerOrgRoles, setViewerOrgRoles] = useState<string[]>([]);
  const [viewerAccRoles, setViewerAccRoles] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const locationIdNumber = useMemo(() => {
    if (!location_id) return null;
    const parsed = Number(location_id);
    return Number.isNaN(parsed) ? null : parsed;
  }, [location_id]);

  const handleCommonAuthNav = useCallback(
    (resStatus?: number, body?: { status_code?: number; message?: string }) => {
      if (
        resStatus === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return true;
      }

      if (resStatus === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return true;
      }

      return false;
    },
    [navigate],
  );

  const isOrgAdmin = useMemo(() => viewerOrgRoles.some((r) => r?.toLowerCase() === 'admin'), [viewerOrgRoles]);
  const isAccAdmin = useMemo(() => viewerAccRoles.some((r) => r?.toLowerCase() === 'admin'), [viewerAccRoles]);
  const isAccMember = useMemo(() => viewerAccRoles.some((r) => r?.toLowerCase() === 'member'), [viewerAccRoles]);

  const canEditLocation = isOrgAdmin || isAccAdmin || isAccMember;
  const canDeleteLocation = isOrgAdmin || isAccAdmin;

  const fetchLocationDetails = useCallback(async () => {
    if (!org_path_name || !acc_path_name || locationIdNumber === null) return;

    const opts: RequestOptions = {
      endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/locations/${encodeURIComponent(String(locationIdNumber))}`,
      method: 'GET',
    };

    try {
      const res = await apiFetchRequest(opts);
      if (!res) {
        fail();
        return;
      }

      const body = res.body as LocationDetailsResponse | undefined;
      if (handleCommonAuthNav(res.status, body)) return;

      if (res.isError || res.status !== 200) {
        fail();
        return;
      }

      if (body?.account && body?.location) {
        setAccount(body.account);
        setLocation(body.location);
        setViewerOrgRoles(Array.isArray(body.user_org_roles) ? body.user_org_roles : []);
        setViewerAccRoles(Array.isArray(body.user_acc_roles) ? body.user_acc_roles : []);
        setErrorText(null);
      } else {
        fail();
      }
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, fail, handleCommonAuthNav, locationIdNumber, org_path_name]);

  useEffect(() => {
    if (!org_path_name || !acc_path_name || locationIdNumber === null) {
      navigate('/404.html');
      return;
    }
    void fetchLocationDetails();
  }, [acc_path_name, fetchLocationDetails, locationIdNumber, navigate, org_path_name]);

  const handleOpenEditDrawer = useCallback(() => setIsEditDrawerOpen(true), []);

  const handleCloseEditDrawer = useCallback(() => {
    if (isSubmitting) return;
    setIsEditDrawerOpen(false);
  }, [isSubmitting]);

  const handleUpdateLocation = useCallback(
    async (payload: LocationDrawerPayload) => {
      if (!org_path_name || !acc_path_name || locationIdNumber === null) return;

      setIsSubmitting(true);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/locations/${encodeURIComponent(String(locationIdNumber))}`,
          method: 'PUT',
          body: payload,
        });

        if (!res) {
          fail();
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
          fail();
          return;
        }

        setIsEditDrawerOpen(false);
        await fetchLocationDetails();
      } catch {
        fail();
      } finally {
        setIsSubmitting(false);
      }
    },
    [acc_path_name, apiFetchRequest, fail, fetchLocationDetails, handleCommonAuthNav, locationIdNumber, org_path_name],
  );

  const handleDeleteLocation = useCallback(async () => {
    if (!org_path_name || !acc_path_name || locationIdNumber === null) return;

    const confirmed = window.confirm('Are you sure you want to delete this location? This action cannot be undone.');
    if (!confirmed) return;

    setIsDeleting(true);

    try {
      const res = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/locations/${encodeURIComponent(String(locationIdNumber))}`,
        method: 'DELETE',
      });

      if (!res) {
        fail();
        return;
      }

      const body = res.body as { message?: string; detail?: string; status_code?: number };
      if (handleCommonAuthNav(res.status, body)) return;

      if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
        fail();
        return;
      }

      navigate(`/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/locations/`);
    } catch {
      fail();
    } finally {
      setIsDeleting(false);
    }
  }, [acc_path_name, apiFetchRequest, fail, handleCommonAuthNav, locationIdNumber, navigate, org_path_name]);

  if ((isLoading && !account && !errorText) || locationIdNumber === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account || !location) return <></>;

  const editButton = canEditLocation ? (
    <GhostButton
      sxProps={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        alignSelf: 'stretch',
        width: { xs: '100%', md: '135px' },
        '& .MuiButton-startIcon': {
          height: '18px',
          width: '18px',
          backgroundImage: `url(${theme.images.editIcon.default})`,
        },
        '&:hover': {
          background: 'transparent',
          color: theme.buttons.ghost.hover.color,
          border: '2px solid',
          '& .MuiButton-startIcon': {
            height: '18px',
            width: '18px',
            backgroundImage: `url(${theme.images.editIcon.hover})`,
          },
        },
      }}
      additionalProps={{
        startIcon: <></>,
        style: { border: 'none', height: '48px' },
        disabled: isSubmitting || isDeleting,
      }}
      onClick={handleOpenEditDrawer}
      buttonText="Edit"
    />
  ) : null;

  const deleteButton = false && canDeleteLocation ? (
    <BorderedButton
      sxProps={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        alignSelf: 'stretch',
        borderColor: theme.buttons.ghost.color,
        width: { xs: '100%', md: '135px' },
        '& .MuiButton-startIcon': {
          height: '18px',
          width: '18px',
          backgroundImage: `url(${theme.images.deleteIcon.default})`,
        },
        '&:hover': {
          background: 'transparent',
          color: theme.buttons.ghost.hover.color,
          border: '2px solid',
          '& .MuiButton-startIcon': {
            height: '18px',
            width: '18px',
            backgroundImage: `url(${theme.images.deleteIcon.hover})`,
          },
        },
      }}
      additionalProps={{ startIcon: <></>, disabled: isSubmitting || isDeleting }}
      onClick={handleDeleteLocation}
      buttonText={isDeleting ? 'Deleting...' : 'Delete'}
    />
  ) : null;

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: '40px',
      }}
    >
      <Box sx={{ mt: 0 }}>
        <AccountLocationNameDescription
          location={location}
          variant="card"
          actionButtons={
            <>
              {editButton}
              {deleteButton}
            </>
          }
        />
      </Box>

      <LocationDrawer
        open={isEditDrawerOpen}
        mode="edit"
        initialLocation={location}
        onClose={handleCloseEditDrawer}
        onSubmit={handleUpdateLocation}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
}
